import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { CompanyRate } from 'src/app/modules/companies/_types/company-rate';
import { CompanyRatesService } from 'src/app/modules/companies/_services/companies-rates.service';
import { CompaniesService } from 'src/app/modules/companies/_services/companies.service';
import { ContractsService } from 'src/app/modules/companies/_services/contracts.service';
import { InsuranceCompaniesService } from 'src/app/modules/insurance-companies/_services/insurance-companies.service';
import { LoginService } from 'src/app/modules/login/login.service';

@Component({
    selector: 'app-rate-company',
    templateUrl: './rate-company.component.html',
    styleUrls: ['./rate-company.component.scss'],
    standalone: false
})
export class RateCompanyComponent {

  public rateCompanyFormGroup!: RxFormGroup;

  public rateCompany: CompanyRate = new CompanyRate();
  public companies: any[] = [];
  public contracts: any[] = [];
  public insuranceCompanies: any[] = [];  
  public selectedContracts: any[] = [];
  public selectedInsuranceCompanies: any[] = [];

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;


  constructor(
    private companyRatesService: CompanyRatesService,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private dynamicDialogRef: DynamicDialogRef,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private companiesService: CompaniesService,
    private contractsService: ContractsService,
    private insuranceCompaniesService: InsuranceCompaniesService,
    private loginService: LoginService
  ) {
    this.rateCompanyFormGroup = this.formBuilder.formGroup<CompanyRate>(this.rateCompany) as RxFormGroup;
  }

  ngOnInit(): void {
    if (this.config.data.rateCompanyId) {
      this.loadRateCompany(this.config.data.rateCompanyId);
    } else {
      this.rateCompany.rateId = this.config.data.rateId;
    }
    this.loadCompanies();
    this.loadInsuranceCompanies();

    this.allowWrite = this.loginService.hasPermission('COMPANY_RATES.WRITE');
    this.allowDelete = this.loginService.hasPermission('COMPANY_RATES.DELETE');
  }

  async loadRateCompany(id: string) {
    this.rateCompany = await firstValueFrom(this.companyRatesService.get(id));            
    this.rateCompanyFormGroup.patchValue(this.rateCompany);

    this.loadContracts();    
  }

  async loadCompanies() {
    let response = await firstValueFrom(this.companiesService.all());

    this.companies = response.data;
  }

  async loadContracts() {
    let filters = {
      companyId: new Filter(FilterOperation.equals, 'companyId', "", this.rateCompanyFormGroup.value.companyId)
    };

    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.contractsService.all(params));
    this.contracts = response.data;    
  }

  async loadInsuranceCompanies() {    
    let response = await firstValueFrom(this.insuranceCompaniesService.all());
    this.insuranceCompanies = response.data;    
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.rateCompanyFormGroup);
    if (valid) {
      this.rateCompany = Object.assign(this.rateCompany, this.rateCompanyFormGroup.value);

      const companyId = this.rateCompanyFormGroup.value.companyId;
      const contractIds = this.selectedContracts?.length ? this.selectedContracts : [null];
      const insuranceCompanyIds = this.selectedInsuranceCompanies?.length ? this.selectedInsuranceCompanies : [null];

      const requests = [];

      for (const contractId of contractIds) {
          for (const insuranceCompanyId of insuranceCompanyIds) {
              this.rateCompany.contractId = contractId;
              this.rateCompany.insuranceCompanyId = insuranceCompanyId;
            
              let rateCompany = await firstValueFrom(this.companyRatesService.save(this.rateCompany));
          }
      }
      this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Compañía guardada correctamente." });
      this.dynamicDialogRef.close();

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  cancel() {
    this.dynamicDialogRef.close();
  }

  delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea desasignar la compañía?",
      header: "Desasignar compañía",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.companyRatesService.delete(this.rateCompany.id).subscribe({
          next: () => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Compañía desasignada correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }
}
